import { useQuery, type TypedDocumentNode } from "@apollo/client"
import React from "react"
import ExampleCarouselBlock, { type ExampleCarouselFragment } from "./example_carousel"
import HeroBlock, { type HeroFragment } from "./hero"
import ImageBlock, { type ImageFragment } from "./image"
import LinkCardsBlock, { type LinkCardsFragment } from "./link_cards"
import LinkCarouselBlock, { type LinkCarouselFragment } from "./link_carousel"
import LinkColumnsBlock, { type LinkColumnsFragment } from "./link_columns"
import QuoteCardBlock, { type QuoteCardFragment } from "./quote_card"
import QuoteSectionBlock, { type QuoteSectionFragment } from "./quote_section"
import RegularHeroBlock, { type RegularHeroFragment } from "./regular_hero"
import SlidingCarouselBlock, { type SlidingCarouselFragment } from "./sliding_carousel"
import VideoBlock, { type VideoFragment } from "./video"
import { graphql, type FragmentType, getFragment } from "@/api"
import { FrontpageBlockableKind } from "@/api/graphql"

const frontpageDoc = graphql(`
  query FrontpageVersionDisplay($id: ID!) {
    frontpageVersion(id: $id) {
      blocks {
        blockableType

        blockable {
          ...FrontpageBlockable
        }
      }
    }
  }
`)

export const BlockableFragment = graphql(`
  fragment FrontpageBlockable on FrontpageBlockable {
    __typename

    ...FrontpagesImage
    ...FrontpagesVideo
    ...FrontpagesHero
    ...FrontpagesRegularHero
    ...FrontpagesExampleCarousel
    ...FrontpagesLinkCarousel
    ...FrontpagesLinkCards
    ...FrontpagesLinkColumns
    ...FrontpagesQuoteCard
    ...FrontpagesQuoteSection
    ...FrontpagesSlidingCarousel
  }
`)

export interface BlockProps<T extends TypedDocumentNode> {
  demo?: boolean
  index: number
  blockable: FragmentType<T>
  businessRole: boolean
  heroV2?: boolean
}

const exhaustiveGuard = (_: never): never => {
  throw new Error("Unhandled block type")
}

export const BlockRenderer = (
  type: FrontpageBlockableKind,
  blockableFragment: FragmentType<typeof BlockableFragment>,
  index: number,
  businessRole: boolean,
  nextType?: FrontpageBlockableKind,
  demo?: boolean,
  regularHeroAbTestV2?: boolean
) => {
  const blockable = getFragment(BlockableFragment, blockableFragment)

  const props = { demo, index, businessRole }

  switch (type) {
    case FrontpageBlockableKind.image:
      return <ImageBlock {...props} blockable={blockable as FragmentType<typeof ImageFragment>} />
    case FrontpageBlockableKind.video:
      return <VideoBlock {...props} blockable={blockable as FragmentType<typeof VideoFragment>} />
    case FrontpageBlockableKind.hero:
      return <HeroBlock {...props} blockable={blockable as FragmentType<typeof HeroFragment>} />
    case FrontpageBlockableKind.regularhero:
      return (
        <RegularHeroBlock
          {...props}
          blockable={blockable as FragmentType<typeof RegularHeroFragment>}
          heroV2={regularHeroAbTestV2}
        />
      )
    case FrontpageBlockableKind.examplecarousel:
      return <ExampleCarouselBlock {...props} blockable={blockable as FragmentType<typeof ExampleCarouselFragment>} />
    case FrontpageBlockableKind.slidingcarousel:
      return <SlidingCarouselBlock {...props} blockable={blockable as FragmentType<typeof SlidingCarouselFragment>} />
    case FrontpageBlockableKind.linkcarousel:
      return (
        <LinkCarouselBlock
          {...props}
          blockable={blockable as FragmentType<typeof LinkCarouselFragment>}
          divider={type === nextType}
        />
      )
    case FrontpageBlockableKind.linkcardblock:
      return <LinkCardsBlock {...props} blockable={blockable as FragmentType<typeof LinkCardsFragment>} />
    case FrontpageBlockableKind.linkcolumnblock:
      return <LinkColumnsBlock {...props} blockable={blockable as FragmentType<typeof LinkColumnsFragment>} />
    case FrontpageBlockableKind.quotecard:
      return <QuoteCardBlock {...props} blockable={blockable as FragmentType<typeof QuoteCardFragment>} />
    case FrontpageBlockableKind.quotesection:
      return <QuoteSectionBlock {...props} blockable={blockable as FragmentType<typeof QuoteSectionFragment>} />
    default:
      return exhaustiveGuard(type)
  }
}

interface FrontpageProps {
  id: string
  businessRole: boolean
  regularHeroAbTestV2: boolean
}

const Frontpage: React.FC<FrontpageProps> = ({ id, businessRole, regularHeroAbTestV2 }) => {
  const results = useQuery(frontpageDoc, { variables: { id } })
  const frontpage = results.data?.frontpageVersion
  const blocks = frontpage?.blocks || []

  return (
    <main id="main">
      <div className="space-y-16 pb-16 pt-8">
        {blocks.map((block, i) => (
          <div key={i} className={`frontpage-block ${block.blockableType}-${i}`}>
            {BlockRenderer(
              block.blockableType,
              block.blockable,
              i,
              businessRole,
              blocks[i + 1]?.blockableType,
              undefined,
              regularHeroAbTestV2
            )}
          </div>
        ))}
      </div>
    </main>
  )
}

export default Frontpage
